import styled from 'styled-components'

const INK = 'var(--vo-ink)'
const MUTED = 'var(--vo-slate)'
const BLUE = 'var(--vo-celeste)'
const BORDER = 'var(--vo-line)'
const LABEL_COLOR = 'var(--vo-ink-2)'

// ── Stepper ──────────────────────────────────────────────────────────────────

export const StepperRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
`

export const StepItem = styled.div`
  display: flex;
  align-items: center;
`

export const StepColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 160px;
`

export const StepCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $done, $active }) =>
    $done ? 'var(--vo-success)' :
    $active ? 'linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri))' :
    'white'};
  border: ${({ $done, $active }) => ($done || $active) ? 'none' : `2px solid ${BORDER}`};
`

export const StepCircleNum = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${({ $active }) => $active ? 'white' : 'var(--vo-steel)'};
`

export const StepLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  color: ${({ $active }) => $active ? BLUE : MUTED};
`

export const StepConnector = styled.div`
  width: 60px;
  height: 2px;
  background: var(--vo-cloud);
  margin-bottom: 22px;
  flex-shrink: 0;
`

// ── Step content ──────────────────────────────────────────────────────────────

export const StepContentWrapper = styled.div`
  max-width: 560px;
  margin: 0 auto;
`

export const StepCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

export const StepTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 4px;
`

export const StepSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0 0 24px;
`

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
`

export const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${LABEL_COLOR};
`

// ── Tier info card ────────────────────────────────────────────────────────────

export const TierCard = styled.div`
  background: ${({ $tier }) =>
    $tier === 1 ? 'var(--vo-danger-10)' :
    $tier === 2 ? 'var(--vo-heatmap-risky)' :
    'var(--vo-success-10)'};
  border: 1px solid ${({ $tier }) =>
    $tier === 1 ? 'var(--vo-danger-20)' :
    $tier === 2 ? 'color-mix(in srgb, var(--vo-warning) 25%, transparent)' :
    'var(--vo-success-20)'};
  border-radius: 10px;
  padding: 14px 16px;
  margin-top: 4px;
`

export const TierCardTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $tier }) =>
    $tier === 1 ? 'var(--vo-danger)' :
    $tier === 2 ? 'var(--vo-warning)' :
    'var(--vo-success)'};
  margin: 0 0 3px;
`

export const TierCardDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`

// ── Maturity dots ─────────────────────────────────────────────────────────────

export const MaturityHeader = styled.div`
  margin-bottom: 20px;
`

export const MaturityLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${LABEL_COLOR};
  margin: 0 0 4px;
  strong { color: ${BLUE}; }
`

export const MaturityDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0 0 12px;
`

export const MaturityDotsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const MaturityDotCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`

export const MaturityDot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  background: ${({ $active }) => $active ? BLUE : 'var(--vo-line)'};
  border: 2px solid ${({ $active }) => $active ? 'var(--vo-cobalto)' : 'transparent'};
  box-shadow: ${({ $active }) => $active ? '0 0 0 3px color-mix(in srgb, var(--vo-info) 30%, transparent)' : 'none'};
  transition: background 0.15s, box-shadow 0.15s;
`

export const MaturityDotLabel = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 10px;
  color: var(--vo-steel);
`

// ── Framework combobox ────────────────────────────────────────────────────────

export const FrameworkDropdownItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`

export const FrameworkItemName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`

export const FrameworkItemMeta = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`

// ── Review grid ───────────────────────────────────────────────────────────────

export const ReviewGrid = styled.div`
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;
`

export const ReviewCell = styled.div`
  padding: 14px 18px;
  border-bottom: ${({ $last }) => $last ? 'none' : '1px solid var(--vo-mist)'};
`

export const ReviewCellLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 4px;
`

export const ReviewCellValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${INK};
  margin: 0;
`

export const ReviewCellValueColored = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $tier }) =>
    $tier === 1 ? 'var(--vo-danger)' :
    $tier === 2 ? 'var(--vo-warning)' :
    'var(--vo-success)'};
  margin: 0;
`

// ── Info alert ────────────────────────────────────────────────────────────────

export const InfoAlert = styled.div`
  background: var(--vo-capri-50);
  border: 1px solid color-mix(in srgb, var(--vo-info) 20%, transparent);
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
`

export const InfoAlertText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-celeste);
  margin: 0;
  strong { color: var(--vo-celeste); }
`

// ── Nav buttons ───────────────────────────────────────────────────────────────

export const NavWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
`

export const NavRight = styled.div`
  display: flex;
  gap: 10px;
`

export const CancelBtn = styled.button`
  height: 40px;
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: ${MUTED};
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

export const BackBtn = styled.button`
  height: 40px;
  display: inline-flex;
  align-items: center;
  background: white;
  color: ${BLUE};
  border: 1.5px solid ${BLUE};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  &:hover:not(:disabled) { background: var(--vo-capri-50); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

export const NextBtn = styled.button`
  height: 40px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  &:hover:not(:disabled) { background: var(--vo-cobalto); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`
